import { useRef } from "react";

import type { Placement } from "@floating-ui/react";
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating
} from "@floating-ui/react";

import type { TourPlacementOffset } from "../configs/types";

/**
 * Custom hook to position the floating element of a tour step.
 * It uses the `useFloating` hook from the `@floating-ui/react` library to calculate the position of the floating element based on the reference element and the specified placement.
 * It also includes middleware for offset, flip, shift, and arrow positioning.
 *
 * @param referenceEl
 * @param placement
 * @param placementOffset
 * @returns
 */
export function useTourFloating(
  referenceEl: Element | null,
  placement: Placement,
  placementOffset?: TourPlacementOffset
) {
  const arrowRef = useRef<SVGSVGElement | null>(null);
  const resolvedPlacementOffset = getPlacementOffset(placementOffset);

  const { refs, floatingStyles, middlewareData, context } = useFloating({
    placement,
    elements: { reference: referenceEl ?? undefined },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(resolvedPlacementOffset),
      flip({ fallbackPlacements: ["top", "bottom", "left", "right"] }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef })
    ]
  });

  return { refs, floatingStyles, middlewareData, context, arrowRef };
}

const DEFAULT_PLACEMENT_OFFSET = 12;

function getPlacementOffset(
  placementOffset?: TourPlacementOffset
): TourPlacementOffset {
  if (placementOffset === undefined || typeof placementOffset === "number") {
    return placementOffset ?? DEFAULT_PLACEMENT_OFFSET;
  }

  return {
    ...placementOffset,
    mainAxis: placementOffset.mainAxis ?? DEFAULT_PLACEMENT_OFFSET
  };
}
